/** The terrain layer: dual-grid corner masks, baked into per-chunk textures.
 *
 * Shape of the thing:
 *
 *   world terrain plane
 *        |  displayCorners()      four corner terrains per display cell
 *        v
 *   resolveDisplayCell()          base fill + layers, ascending priority
 *        |
 *        +-- one terrain          -> the fill tile, straight from the atlas
 *        +-- a pre-baked pair     -> the transition tile, straight from the atlas
 *        +-- anything else        -> TileComposer builds it once and caches it
 *        v
 *   chunk RenderTexture (32x32 display cells)  <- re-baked only when a cell in
 *        |                                        it changes
 *        v
 *   one Sprite per chunk
 *
 * The composite path runs on a 2D canvas rather than through a Pixi mask. Two
 * reasons: a mask costs a render pass per instance, and the canvas result is
 * pixel-exact against the reference implementation, which matters because the
 * fills and the masks are both indexed by the same positional variant so a
 * transition tile's solid region has to be identical to the fill that would
 * have been there.
 *
 * Roads, rivers and the decal scatter are baked into the same chunk texture.
 * They never change once the map is generated, and folding them in removes two
 * whole sprite layers from the frame.
 */

import { CanvasSource, Container, Graphics, Rectangle, RenderTexture, Sprite, Texture } from "pixi.js";
import type { Renderer } from "pixi.js";

import type { Atlas } from "../atlas.js";
import type { SnappedCamera } from "../coords.js";
import { TILE_SIZE, cameraWorldRect } from "../coords.js";
import type { ChunkCoord, DisplayPlan } from "./dualgrid.js";
import {
  CHUNK_CELLS, chunkDisplayRange, chunkKey, chunksForWorldCell, chunksInPixelRect,
  DIAG_NE, DIAG_NW, DIAG_SE, DIAG_SW,
  chunkOrigin, connectionMask, displayCornersVisible, displayPlanKey, fillKey, maskKey,
  overlayKey, resolveDisplayCell, riverMouth, riverMouthsInto, transitionKey, variantIndex,
} from "./dualgrid.js";

export { chunkOrigin } from "./dualgrid.js";
import { paintDecals } from "./decals.js";
import { frameBounds } from "./frames.js";

const COMPOSITE_PAGE_TILES = 32;             // 32 x 32 tiles of 64px = 2048px
const COMPOSITE_PAGE_PX = COMPOSITE_PAGE_TILES * TILE_SIZE;
const MAX_COMPOSITE_PAGES = 4;

/**
 * Builds the transition tiles the pre-baked pair atlases do not cover, on a 2D
 * canvas, and hands them back as textures cut out of a shared page.
 */
class TileComposer {
  private readonly pages: {
    readonly canvas: HTMLCanvasElement;
    readonly ctx: CanvasRenderingContext2D;
    readonly source: CanvasSource;
    used: number;
    dirty: boolean;
  }[] = [];
  private readonly cache = new Map<string, Texture>();
  private readonly scratch: HTMLCanvasElement;
  private readonly scratchCtx: CanvasRenderingContext2D;
  private exhausted = false;

  constructor(private readonly atlas: Atlas) {
    this.scratch = document.createElement("canvas");
    this.scratch.width = TILE_SIZE;
    this.scratch.height = TILE_SIZE;
    const c = this.scratch.getContext("2d", { willReadFrequently: false });
    if (c === null) throw new Error("terrain: no 2d context for the tile composer");
    c.imageSmoothingEnabled = false;
    this.scratchCtx = c;
  }

  /** `null` when every page is full; the caller falls back to the base fill. */
  get(plan: DisplayPlan, variant: number): Texture | null {
    const [tl, tr, br, bl] = plan.corners;
    const key = displayPlanKey(tl, tr, br, bl, variant);
    const hit = this.cache.get(key);
    if (hit !== undefined) return hit;

    const slot = this.allocate();
    if (slot === null) return null;

    const { page, x, y } = slot;
    page.ctx.clearRect(x, y, TILE_SIZE, TILE_SIZE);
    if (!this.blit(page.ctx, fillKey(plan.base, variant), x, y)) return null;

    for (const layer of plan.layers) {
      this.scratchCtx.globalCompositeOperation = "source-over";
      this.scratchCtx.clearRect(0, 0, TILE_SIZE, TILE_SIZE);
      if (!this.blit(this.scratchCtx, fillKey(layer.terrain, variant), 0, 0)) continue;
      // The mask is white where the terrain paints and fully transparent
      // elsewhere, so keeping the fill's own pixels only inside the mask is a
      // single destination-in.
      this.scratchCtx.globalCompositeOperation = "destination-in";
      if (!this.blit(this.scratchCtx, maskKey(layer.mask, variant), 0, 0)) continue;
      this.scratchCtx.globalCompositeOperation = "source-over";
      page.ctx.drawImage(this.scratch, x, y);
    }

    page.dirty = true;
    const tex = new Texture({
      source: page.source,
      frame: new Rectangle(x, y, TILE_SIZE, TILE_SIZE),
      label: `terrain.composite.${key}`,
    });
    this.cache.set(key, tex);
    return tex;
  }

  /** Upload every page that changed. Call once before the chunk is rendered. */
  flush(): void {
    for (const p of this.pages) {
      if (!p.dirty) continue;
      p.source.update();
      p.dirty = false;
    }
  }

  private blit(ctx: CanvasRenderingContext2D, key: string, dx: number, dy: number): boolean {
    const f = this.atlas.frame(key);
    if (f === null) return false;
    const bmp = this.atlas.bitmap(f.sheet);
    if (bmp === null) return false;
    // The mask atlas is trimmed - a one-corner stencil is 75% transparent - and
    // anchored at `-trim`, so subtracting the anchor puts the packed rect back
    // where the untrimmed tile had it. Mask 0 is not packed at all; nothing
    // ever asks for it, because a layer with no corners is not a layer.
    ctx.drawImage(bmp, f.x, f.y, f.w, f.h, dx - f.anchorX, dy - f.anchorY, f.w, f.h);
    return true;
  }

  private allocate(): { page: TileComposer["pages"][number]; x: number; y: number } | null {
    const perPage = COMPOSITE_PAGE_TILES * COMPOSITE_PAGE_TILES;
    let page = this.pages[this.pages.length - 1];
    if (page === undefined || page.used >= perPage) {
      if (this.pages.length >= MAX_COMPOSITE_PAGES) {
        if (!this.exhausted) {
          this.exhausted = true;
          // Not thrown: a map this varied should still draw, just with the
          // rarest junctions falling back to their base fill.
          console.warn("terrain: composite atlas full; rare junctions will not composite");
        }
        return null;
      }
      page = this.newPage();
      this.pages.push(page);
    }
    const i = page.used++;
    return {
      page,
      x: (i % COMPOSITE_PAGE_TILES) * TILE_SIZE,
      y: Math.floor(i / COMPOSITE_PAGE_TILES) * TILE_SIZE,
    };
  }

  private newPage(): TileComposer["pages"][number] {
    const canvas = document.createElement("canvas");
    canvas.width = COMPOSITE_PAGE_PX;
    canvas.height = COMPOSITE_PAGE_PX;
    const ctx = canvas.getContext("2d");
    if (ctx === null) throw new Error("terrain: no 2d context for the composite atlas");
    ctx.imageSmoothingEnabled = false;
    const source = new CanvasSource({
      resource: canvas,
      scaleMode: "nearest",
      autoGenerateMipmaps: false,
      label: "terrain.composite",
    });
    return { canvas, ctx, source, used: 0, dirty: false };
  }

  destroy(): void {
    for (const t of this.cache.values()) t.destroy(false);
    this.cache.clear();
    for (const p of this.pages) p.source.destroy();
    this.pages.length = 0;
  }
}

// ---------------------------------------------------------------- the layer

/** One ground's look: its painted tiles by id, and the tint behind them. */
export interface GroundWash {
  readonly id: string;
  readonly colour: number;
  readonly alpha: number;
}

export interface TerrainPlanes {
  readonly width: number;
  readonly height: number;
  readonly levels: number;
  readonly terrain: Uint8Array;
  readonly road: Uint8Array;
  readonly river: Uint8Array;
  /** Cells the viewer has never seen; their terrain byte is zero and must not
   *  be trusted. Water (index 0) would otherwise flood every unexplored edge. */
  readonly visibility: Uint8Array;
  /**
   * Section 31 - the magic-ground slot per cell, and the wash each slot wears.
   *
   * A wash rather than a tile set: magic ground lies over whatever terrain is
   * already there, so it needs no transition to its neighbours - which is the
   * whole reason it is not a terrain kind. `wash` is indexed by the slot in the
   * cell, so slot 0 is `null` and draws nothing.
   *
   * Both optional: a host that has not been taught about magic ground draws the
   * map it always drew.
   */
  readonly ground?: Uint8Array | undefined;
  readonly wash?: readonly (GroundWash | null)[] | undefined;
}

export interface TerrainOptions {
  /** Chunk textures resident at once. Each is 2048x2048, so 8 is ~134 MB. */
  readonly maxResidentChunks?: number;
  readonly decals?: boolean;
}

interface ChunkEntry {
  readonly coord: ChunkCoord;
  readonly texture: RenderTexture;
  readonly sprite: Sprite;
  stamp: number;
}

/**
 * The eight arms a road tile can have, as (dx, dy, connection bit): the
 * direction from the tile centre to the crossing, cardinal ones to an edge
 * midpoint, diagonal ones to a corner (so a unit step reaches the tile edge
 * in both cases).
 */
const ARMS: readonly (readonly [number, number, number])[] = Object.freeze([
  [0, -1, 1], [1, 0, 2], [0, 1, 4], [-1, 0, 8],
  [1, -1, DIAG_NE], [1, 1, DIAG_SE], [-1, 1, DIAG_SW], [-1, -1, DIAG_NW],
]);

/** How strongly a coarser surface bleeds back over the finer one at a join. */
const SEAM_BLEND_ALPHA = 0.55;

/*
 * There is deliberately NO edge feathering on roads. An earlier version
 * stamped the road kind's isolated-tile disc a few times outside every open
 * edge as shed stones, and the result on screen was a halo of light-grey
 * discs 11-16 px beyond both banks of every road, on grass, dirt, lava and
 * snow alike - 9-28% of each verge row - where the classic's road ends in a crisp
 * one-pixel darker line with nothing outside it. The band's own worn verge is
 * drawn into the atlas by build_overlays.py.
 */

export class TerrainLayer {
  readonly container = new Container({ label: "terrain" });

  private readonly composer: TileComposer;
  private readonly chunks = new Map<string, ChunkEntry>();
  private readonly stale = new Set<string>();
  private planes: TerrainPlanes | null = null;
  private stamp = 0;
  /** How the chunk textures are sampled on the glass; see `setScaleMode`. */
  private scaleMode: "nearest" | "linear" = "nearest";
  private readonly maxResident: number;
  private readonly withDecals: boolean;

  constructor(
    private readonly atlas: Atlas,
    private readonly renderer: Renderer,
    opts: TerrainOptions = {},
  ) {
    this.composer = new TileComposer(atlas);
    this.maxResident = opts.maxResidentChunks ?? 8;
    this.withDecals = opts.decals ?? true;
  }

  /** A whole new map, or a new level: everything is stale. */
  setPlanes(planes: TerrainPlanes): void {
    this.planes = planes;
    this.invalidateAll();
  }

  /**
   * Adopt a fresh observation of the same map without invalidating anything.
   *
   * `PlayerObservation` hands back new typed arrays every call, so the layer has
   * to be re-pointed at them each turn; the caller has already diffed the old
   * arrays against the new ones and invalidated the cells that actually moved.
   * Calling `setPlanes` here instead would re-bake every resident chunk on
   * every step a hero takes.
   */
  refreshPlanes(planes: TerrainPlanes): void {
    this.planes = planes;
  }

  /**
   * Sampling for the chunk textures - the ones the glass sees. `"linear"`
   * while the presenter's frame is minified (see `ZOOM_OUT_STEPS` in `coords.ts`),
   * `"nearest"` otherwise. The composite page the chunks are painted FROM is
   * left alone: it is only ever read at 1:1. Chunks built later take the mode
   * in force. Idempotent and cheap: a chunk already in the mode is skipped.
   */
  setScaleMode(mode: "nearest" | "linear"): void {
    this.scaleMode = mode;
    for (const chunk of this.chunks.values()) {
      const source = chunk.texture.source;
      if (source.scaleMode === mode) continue;
      source.scaleMode = mode;
      source.style.update();
    }
  }

  invalidateAll(): void {
    for (const key of this.chunks.keys()) this.stale.add(key);
  }

  invalidateCell(x: number, y: number, z: number): void {
    for (const c of chunksForWorldCell(x, y, z)) this.stale.add(chunkKey(c));
  }

  /** Ensure every chunk the camera can see is baked and parented. */
  update(cam: SnappedCamera, z: number): void {
    const p = this.planes;
    if (p === null) return;

    const want = chunksInPixelRect(cameraWorldRect(cam), p.width, p.height, z, TILE_SIZE);
    const wanted = new Set(want.map(chunkKey));

    this.stamp++;
    for (const coord of want) {
      const key = chunkKey(coord);
      let entry = this.chunks.get(key);
      if (entry === undefined) {
        entry = this.createChunk(coord);
        this.chunks.set(key, entry);
        this.stale.add(key);
      }
      entry.stamp = this.stamp;
      if (this.stale.has(key)) {
        this.bake(entry, p);
        this.stale.delete(key);
      }
      if (entry.sprite.parent === null) this.container.addChild(entry.sprite);
    }

    for (const [key, entry] of this.chunks) {
      if (wanted.has(key)) continue;
      if (entry.sprite.parent !== null) this.container.removeChild(entry.sprite);
    }
    this.evict();
  }

  private createChunk(coord: ChunkCoord): ChunkEntry {
    const size = CHUNK_CELLS * TILE_SIZE;
    const texture = RenderTexture.create({
      width: size,
      height: size,
      scaleMode: this.scaleMode,
      antialias: false,
      autoGenerateMipmaps: false,
      label: `terrain.chunk.${chunkKey(coord)}`,
    });
    const sprite = new Sprite({ texture, label: `chunk ${chunkKey(coord)}` });
    const o = chunkOrigin(coord, TILE_SIZE);
    sprite.position.set(o.x, o.y);
    return { coord, texture, sprite, stamp: 0 };
  }

  private bake(entry: ChunkEntry, p: TerrainPlanes): void {
    const scene = new Container();
    const o = chunkOrigin(entry.coord, TILE_SIZE);
    const range = chunkDisplayRange(entry.coord, p.width, p.height);

    for (let dy = range.dy0; dy < range.dy1; dy++) {
      for (let dx = range.dx0; dx < range.dx1; dx++) {
        const tex = this.tileTexture(p, entry.coord.z, dy, dx);
        if (tex === null) continue;
        const s = new Sprite(tex);
        s.position.set(dx * TILE_SIZE - TILE_SIZE / 2 - o.x, dy * TILE_SIZE - TILE_SIZE / 2 - o.y);
        scene.addChild(s);
      }
    }

    // Roads, rivers and decals live on WORLD cells, which straddle the display
    // grid, so the sweep runs one cell wider than the chunk and lets the render
    // texture clip. Both neighbours draw their half of a straddling tile.
    const cx0 = Math.max(0, range.dx0 - 1);
    const cy0 = Math.max(0, range.dy0 - 1);
    const cx1 = Math.min(p.width, range.dx1 + 1);
    const cy1 = Math.min(p.height, range.dy1 + 1);
    const plane = p.width * p.height * entry.coord.z;

    // Every river before any road, not both per cell: a diagonal road piece
    // overhangs into the two cells flanking its corner, and a river running
    // through one of those would otherwise be painted over the road's wedge
    // when the sweep reached it.
    for (const [set, data] of [["river", p.river], ["road", p.road]] as const) {
      for (let y = cy0; y < cy1; y++) {
        for (let x = cx0; x < cx1; x++) {
          const i = plane + y * p.width + x;
          if ((p.visibility[i] ?? 0) === 0) continue;
          this.addOverlay(scene, p, set, data, x, y, variantIndex(y, x), o, plane);
        }
      }
    }
    if (this.withDecals) {
      paintDecals(scene, this.atlas, p, entry.coord.z,
        { x0: cx0, y0: cy0, x1: cx1, y1: cy1 }, o);
    }

    // The magic ground, over the tiles and the overlays and under the decals'
    // own shadows - it is the GROUND that is enchanted, and a river running
    // through a clover field is still a river. Drawn on the world grid like
    // the overlays, and only where the viewer has been: a wash on unexplored
    // ground would map the enchantments of a country he has never walked.
    paintGroundWash(scene, this.atlas, p, { x0: cx0, y0: cy0, x1: cx1, y1: cy1 }, o, plane);

    this.composer.flush();
    this.renderer.render({ container: scene, target: entry.texture, clear: true });
    scene.destroy({ children: true, context: true });
  }

  private tileTexture(
    p: TerrainPlanes, z: number, dy: number, dx: number,
  ): Texture | null {
    // Unexplored corners carry a masked-to-zero terrain byte, and zero is
    // water; `displayCornersVisible` keeps them from voting. See its comment.
    const [tl, tr, br, bl] = displayCornersVisible(
      p.terrain, p.visibility, p.width, p.height, z, dy, dx,
    );
    const variant = variantIndex(dy, dx);
    const plan = resolveDisplayCell(tl, tr, br, bl);

    if (plan.layers.length === 0) {
      return this.atlas.tryTexture(fillKey(plan.base, variant));
    }
    if (plan.layers.length === 1) {
      const layer = plan.layers[0]!;
      const k = transitionKey(layer.terrain, plan.base, layer.mask, variant);
      if (k !== null) {
        const t = this.atlas.tryTexture(k);
        if (t !== null) return t;
      }
    }
    return this.composer.get(plan, variant)
      ?? this.atlas.tryTexture(fillKey(plan.base, variant));
  }

  private addOverlay(
    scene: Container, p: TerrainPlanes, set: "road" | "river", planeData: Uint8Array,
    x: number, y: number, variant: number, o: { x: number; y: number }, base: number,
  ): void {
    const kind = planeData[base + y * p.width + x] ?? 0;
    if (kind === 0) {
      // A water cell draws the end of every river that flows into it, so the
      // channel reaches the sea as drawn rather than stopping on the beach
      // the coast transition paints over this cell. See `riverMouth`.
      if (set !== "river") return;
      for (const [mouthKind, bit] of riverMouthsInto(
        planeData, p.terrain, p.visibility, p.width, p.height, x, y, base)) {
        this.placeOverlay(scene, set, mouthKind, bit, x, y, variant, o, 1);
      }
      return;
    }
    let mask = connectionMask(planeData, p.width, p.height, x, y, base);
    if (set === "river") {
      mask |= riverMouth(planeData, p.terrain, p.visibility, p.width, p.height, x, y, base);
    }
    this.placeOverlay(scene, set, kind, mask, x, y, variant, o, 1);

    // Where two *different* surfaces meet - the generator lays cobble between
    // towns and gravel on the spurs, and they change kind mid-run - the two
    // pieces butt against each other and the join is a straight seam across the
    // road, which reads as a rendering fault rather than as a change of paving.
    // The coarser surface's end cap is drawn a second time on the finer cell,
    // fading out, so the materials interleave. Only the lower-numbered cell
    // does it, so the pair is drawn once and the result does not depend on
    // which cell the chunk sweep reached first. A corner neighbour counts
    // only when the join is drawn as a corner arm (both flanks bare), and its
    // end cap is the single-corner-arm frame of the diagonal atlas.
    const at = (cx: number, cy: number): number =>
      cx < 0 || cy < 0 || cx >= p.width || cy >= p.height ? 0 : (planeData[base + cy * p.width + cx] ?? 0);
    for (const [dx, dy, bit] of ARMS) {
      const other = at(x + dx, y + dy);
      if (other === 0 || other <= kind) continue;
      if (dx !== 0 && dy !== 0 && (at(x + dx, y) !== 0 || at(x, y + dy) !== 0)) continue;
      this.placeOverlay(scene, set, other, bit, x, y, variant, o, SEAM_BLEND_ALPHA);
    }
  }

  private placeOverlay(
    scene: Container, set: "road" | "river", kind: number, mask: number,
    x: number, y: number, variant: number, o: { x: number; y: number }, alpha: number,
  ): void {
    const key = overlayKey(set, kind, mask, variant);
    if (key === null) return;
    const f = this.atlas.frame(key);
    if (f === null) return;
    // Road and river frames are TRIMMED: an end cap is mostly transparent and
    // the packer cropped it, anchoring at `-trim` so the untrimmed origin comes
    // back. Placing the packed rect at the cell corner instead would slide
    // every road piece by its own crop. And the diagonal atlas's cells are
    // LARGER than a tile - a band centred on a corner spills into the two
    // flanking cells, and the cell carries that wedge as an overhang - so the
    // untrimmed origin sits half the excess before the tile corner.
    const over = (f.srcW - TILE_SIZE) / 2;
    const b = frameBounds(x * TILE_SIZE - over, y * TILE_SIZE - over, f);
    const s = new Sprite(this.atlas.texture(key));
    s.position.set(b.x - o.x, b.y - o.y);
    s.alpha = alpha;
    scene.addChild(s);
  }

  private evict(): void {
    if (this.chunks.size <= this.maxResident) return;
    const byAge = [...this.chunks.entries()].sort((a, b) => a[1].stamp - b[1].stamp);
    for (const [key, entry] of byAge) {
      if (this.chunks.size <= this.maxResident) break;
      if (entry.stamp === this.stamp) continue;
      if (entry.sprite.parent !== null) this.container.removeChild(entry.sprite);
      entry.sprite.destroy();
      entry.texture.destroy(true);
      this.chunks.delete(key);
      this.stale.delete(key);
    }
  }

  destroy(): void {
    for (const entry of this.chunks.values()) {
      entry.sprite.destroy();
      entry.texture.destroy(true);
    }
    this.chunks.clear();
    this.composer.destroy();
    this.container.destroy({ children: true, context: true });
  }
}

/**
 * Section 31 - one translucent quad per enchanted cell.
 *
 * Baked into the terrain chunk like the roads and the decals, so it costs
 * nothing per frame: the ground is painted by the generator and never changes,
 * and a chunk that did change would be re-baked anyway.
 *
 * Exported so it can be driven without a GPU - it returns how many cells it
 * painted, which is the only thing worth asserting about a wash and is not
 * readable off a `Graphics` afterwards.
 */
export function paintGroundWash(
  scene: Container, atlas: Atlas | null, p: TerrainPlanes,
  box: { x0: number; y0: number; x1: number; y1: number },
  o: { x: number; y: number }, base: number,
): number {
  const ground = p.ground;
  const wash = p.wash;
  if (ground === undefined || wash === undefined || wash.length <= 1) return 0;
  const g = new Graphics();
  let painted = 0;
  let flat = false;
  for (let y = box.y0; y < box.y1; y++) {
    for (let x = box.x0; x < box.x1; x++) {
      const i = base + y * p.width + x;
      // Never on ground the viewer has not seen: a wash on unexplored country
      // would map the enchantments of a place he has never walked.
      if ((p.visibility[i] ?? 0) === 0) continue;
      const slot = ground[i] ?? 0;
      const paint = slot === 0 ? null : wash[slot] ?? null;
      if (paint === null) continue;
      // A patch's rim is drawn thinner than its middle. The ground is a wash
      // and not a tile set, so it has no transition art to its neighbours;
      // without this the edge of a patch is a hard line along a cell boundary
      // and the whole thing reads as a rectangle painted on the map rather
      // than as a place. One cell of feather is enough to lose the grid.
      const edge = (dx: number, dy: number): boolean => {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= p.width || ny >= p.height) return true;
        return (ground[base + ny * p.width + nx] ?? 0) !== slot;
      };
      const rim = edge(-1, 0) || edge(1, 0) || edge(0, -1) || edge(0, 1);
      const alpha = rim ? paint.alpha * 0.55 : paint.alpha;
      // The painted wash, tiled out of the ground's own 4x4 master by cell
      // position exactly as a terrain fill is, so a patch is not one image
      // stamped over and over. A ground with no art - or a host with no atlas,
      // which is every test in this package - falls back to the flat tint,
      // which is what shipped before the paintings were cut and is still
      // better than drawing nothing.
      const tex = atlas === null ? null
        : atlas.tryTexture(`ground.${paint.id}.fill.${String(y % 4)}_${String(x % 4)}`);
      if (tex === null) {
        // A painting IS the ground and is drawn at its authored strength; a
        // tint is only a hint of one and is drawn faint, because a solid slab
        // of flat colour over the map is worse than a wash you can see through.
        g.rect(x * TILE_SIZE - o.x, y * TILE_SIZE - o.y, TILE_SIZE, TILE_SIZE)
          .fill({ color: paint.colour, alpha: alpha * 0.35 });
        flat = true;
      } else {
        const s = new Sprite(tex);
        s.position.set(x * TILE_SIZE - o.x, y * TILE_SIZE - o.y);
        s.alpha = alpha;
        scene.addChild(s);
      }
      painted++;
    }
  }
  if (flat) scene.addChild(g);
  else g.destroy({ context: true });
  return painted;
}
